import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Rpfi26TableResult } from '../../../../core/models/report/rpfi26.model';
import { ParkingVehicleTypeResponse } from '../../../../core/models/vehicle.model';
import { MapperRpfi26Service } from '../../../../core/mappers/report/mapper-rpfi26.service';
import { ApiRpfi26Service } from '../../../../core/services/api/report/api-rpfi26.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-rpfi26',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './rpfi26.component.html',
  styleUrl: './rpfi26.component.css',
})
export class Rpfi26Component implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRpfi26Service);
  private readonly mapper = inject(MapperRpfi26Service);
  private readonly cardApi = inject(ApiParkingCardListService);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  isLoadingVehicleTypes = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'RPFI26.pdf';

  startDate = '';
  endDate = '';
  lastFetchedTime: Date | null = null;
  hasSearched = signal(false);

  vehicleTypes = signal<ParkingVehicleTypeResponse[]>([]);
  vehicleTypeId: number | null = null;
  searchVehicleTypeText = '';
  isVehicleTypeDropdownOpen = false;

  private readonly maxRangeMonths = 2;
  result = signal<Rpfi26TableResult | null>(null);

  constructor() {
    const today = this.toInputDate(new Date());
    this.startDate = today;
    this.endDate = today;

    effect(() => {
      this.siteState.selectedSite()?.id;
      untracked(() => this.clearResult());
    });
  }

  ngOnInit(): void {
    this.loadVehicleTypes();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get rows() {
    return this.result()?.rows ?? [];
  }

  get totals() {
    return this.result()?.totals ?? null;
  }

  get filteredVehicleTypes(): ParkingVehicleTypeResponse[] {
    const q = this.searchVehicleTypeText.trim().toLowerCase();
    const list = this.vehicleTypes();
    if (!q || q === 'ทุกประเภท') return list;
    return list.filter((v) => (v.typeName || '').toLowerCase().includes(q));
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  onFilterChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  selectVehicleType(opt: ParkingVehicleTypeResponse): void {
    this.vehicleTypeId = opt.id;
    this.searchVehicleTypeText = opt.typeName || '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  clearVehicleType(): void {
    this.vehicleTypeId = null;
    this.searchVehicleTypeText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  closeDropdowns(): void {
    if (!this.isVehicleTypeDropdownOpen) return;
    const text = this.searchVehicleTypeText.trim().toLowerCase();
    if (!text || text === 'ทุกประเภท') {
      this.clearVehicleType();
      return;
    }
    const match = this.vehicleTypes().find((v) => (v.typeName || '').toLowerCase() === text);
    if (match) {
      this.vehicleTypeId = match.id;
      this.searchVehicleTypeText = match.typeName || '';
    } else if (this.vehicleTypeId) {
      this.searchVehicleTypeText =
        this.vehicleTypes().find((v) => v.id === this.vehicleTypeId)?.typeName || '';
    } else {
      this.searchVehicleTypeText = '';
    }
    this.isVehicleTypeDropdownOpen = false;
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getRpfi26ForTable({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
      vehicleTypeId: this.vehicleTypeId,
    }).subscribe({
      next: (res) => {
        this.result.set(this.mapper.mapTable(res));
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายงาน RPFI26 ไม่สำเร็จ');
      },
    });
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || this.rows.length === 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows: Record<string, string | number>[] = this.rows.map((r) => ({
        'ลำดับ': r.seq,
        'เลขใบเสร็จ': r.receiptId,
        'ทะเบียน': r.entryLicense,
        'วัน-เวลา เข้า': this.formatDateTime(r.entryDate),
        'วัน-เวลา ชำระ': this.formatDateTime(r.paidDate),
        'ยกออก': r.manualExit,
        'เงินสด': r.cash,
        'QR': r.qr,
        'ชม.ปกติ': r.durationText,
        'ก่อน VAT': r.fee,
        'ภาษี': r.vat,
        'ยอดสุทธิ': r.total,
      }));

      const t = this.totals!;
      exportRows.push({
        'ลำดับ': '',
        'เลขใบเสร็จ': `สรุป ${t.rowCount} รายการ`,
        'ทะเบียน': '',
        'วัน-เวลา เข้า': '',
        'วัน-เวลา ชำระ': '',
        'ยกออก': t.sumManualExit,
        'เงินสด': t.sumCash,
        'QR': t.sumQr,
        'ชม.ปกติ': '',
        'ก่อน VAT': t.sumFee,
        'ภาษี': t.sumVat,
        'ยอดสุทธิ': t.sumTotal,
      });

      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      const extraLines: string[] = [];
      if (this.vehicleTypeId) {
        const vtName = this.vehicleTypes().find((v) => v.id === this.vehicleTypeId)?.typeName;
        if (vtName) {
          extraLines.push(`ประเภทรถ: ${vtName}`);
        }
      }
      await downloadReportExcel(exportRows, {
        reportTitle: 'RPFI26 — รายงานรถบัส',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: this.startDate,
        endDate: this.endDate,
        extraLines,
        sheetName: 'RPFI26',
        fileName: buildReportExcelFilename('RPFI26', siteName, this.startDate, this.endDate),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและช่วงวันที่');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `RPFI26_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api.getRpfi26Pdf({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
      vehicleTypeId: this.vehicleTypeId,
    }).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: (err) => {
        console.error('[API Error] getRpfi26Pdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  private loadVehicleTypes(): void {
    this.isLoadingVehicleTypes.set(true);
    this.cardApi.getParkingVehicleTypes().subscribe({
      next: (list) => {
        this.vehicleTypes.set(list ?? []);
        this.isLoadingVehicleTypes.set(false);
      },
      error: () => {
        this.vehicleTypes.set([]);
        this.isLoadingVehicleTypes.set(false);
      },
    });
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
